import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Callout, Skeleton, tokens } from '@treams/treams-ui';

import GoalCard from './GoalCard';

export default function RemainingGoalsStatus(props: { momentID: string }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useMomentForGoalsQuery({ variables: { id: props.momentID } });
	const [expanded, setExpanded] = useState(false);

	if (!query.data) {
		return <Skeleton.Box height={80} mb={`${tokens.gap.section}px`} />;
	}

	const regularRequired = Math.max(
		1,
		query.data.momentInstance.momentTemplate.goals?.minimumGoals ?? 0,
	);
	const competencyRequired =
		query.data.momentInstance.momentTemplate.goals?.minimumCompetencyGoals ?? 0;

	const regularCount = query.data.momentInstance.goals?.length ?? 0;
	const competencyCount =
		query.data.momentInstance.goals?.filter((goal) => !!goal.competency).length ?? 0;

	const competencyRemaining = Math.max(0, competencyRequired - competencyCount);
	const regularRemaining = Math.max(0, regularRequired - regularCount);

	const title =
		competencyRemaining === 0
			? t('goals.createFromMoment.remaining.simple', {
					regular: regularRemaining,
				})
			: t('goals.createFromMoment.remaining.title', {
					competency: competencyRemaining,
					regular: Math.max(0, regularRemaining - competencyRemaining),
				});

	return (
		<Callout
			color="blue"
			mb={`${tokens.gap.section}px`}
			title={
				<>
					{title} <strong>{localize(query.data.momentInstance.momentTemplate.title).text}</strong>
				</>
			}
			description={`${t('common.dueDate')}: ${moment().format('LL')}`}
			action={
				(query.data.momentInstance.goals?.length ?? 0) > 0 ? (
					expanded ? (
						<Box mt={`${tokens.gap.list}px`}>
							{(query.data.momentInstance.goals ?? []).map((goal) => (
								<Box key={goal.id} mb={`${tokens.gap.list}px`}>
									<GoalCard goal={goal as graphql.Goal} small owner={false} />
								</Box>
							))}
						</Box>
					) : (
						<Box
							style={{ cursor: 'pointer', textDecoration: 'underline' }}
							onClick={() => setExpanded(true)}
						>
							{t('goals.createFromMoment.remaining.chevron')}
						</Box>
					)
				) : undefined
			}
		/>
	);
}
